import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertUprightCapBody, UpdateUprightCapBody, UprightCap } from "./types/uprightCaps";

export const useUprightCapsApi = () => {
  const api = useApi();

  const getAdminUprightCap = useCallback(
    (uprightCapId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/upright_caps/${uprightCapId}`;
      return useSWRMutation<UprightCap, CustomError, string>(url, (url) => api.get<UprightCap>({ url, signal }));
    },
    [api],
  );

  const useAdminUprightCaps = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/upright_caps`, query);
      return useSWRImmutable<Paginated<UprightCap>, CustomError, string>(url, (url) =>
        api.get<Paginated<UprightCap>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminUprightCaps = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/upright_caps`, query);
      return api.get<Paginated<UprightCap>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminUprightCap = useCallback(
    async (body: InsertUprightCapBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/upright_caps`;
      const id = await api.post<{ id: string }, InsertUprightCapBody>(url, body);
      mutateCache([url, "/admin/settings/border/uprights"]);
      return id;
    },
    [api],
  );

  const updateAdminUprightCap = useCallback(
    async (uprightCapId: string, body: UpdateUprightCapBody): Promise<void> => {
      const url = `/admin/settings/border/upright_caps/${uprightCapId}`;
      await api.put<UpdateUprightCapBody>(url, body);
      mutateCache(["/admin/settings/border/upright_caps", "/admin/settings/border/uprights"]);
    },
    [api],
  );

  const updateAdminUprightCapVisibility = useCallback(
    async (uprightCapId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/upright_caps/${uprightCapId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/upright_caps", "/admin/settings/border/uprights"]));
    },
    [api],
  );

  const updateAdminUprightCapOrder = useCallback(
    async (uprightCapId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/upright_caps/${uprightCapId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/upright_caps", "/admin/settings/border/uprights"]));
    },
    [api],
  );

  const deleteAdminUprightCap = useCallback(
    async (uprightCap: UprightCap): Promise<void> => {
      const url = `/admin/settings/border/upright_caps/${uprightCap.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/upright_caps`, "/admin/settings/border/uprights"]);
    },
    [api],
  );

  return {
    getAdminUprightCap,
    insertAdminUprightCap,
    updateAdminUprightCap,
    updateAdminUprightCapVisibility,
    updateAdminUprightCapOrder,
    deleteAdminUprightCap,
    useAdminUprightCaps,
    getAdminUprightCaps,
  };
};
